import React, { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { Link } from "react-router-dom";
import { listarMisAlertas, marcarAlertaLeida, marcarTodasLeidas, contarAlertasNoLeidas } from "./api/alertas";
import "./AlertBell.css";

const ICONOS_EVENTO = {
    precio: "💲",
    version_nueva: "🆕",
    descontinuacion: "🚫",
    financiamiento: "🏦",
    value_score: "📊",
    value_score_ranking: "🏆",
};

function nombreVehiculo(v) {
    if (!v) return "—";
    return `${v.marca} ${v.model} ${v.modelDetail}`;
}

function fmtFecha(ts) {
    if (!ts) return "";
    try {
        return new Date(ts).toLocaleDateString("es-CL", { day: "2-digit", month: "short" });
    } catch {
        return "";
    }
}

export default function AlertBell() {
    const [open, setOpen] = useState(false);
    const [uid, setUid] = useState(null);
    const [count, setCount] = useState(0);
    const [alertas, setAlertas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        const auth = getAuth();
        const unsub = onAuthStateChanged(auth, (u) => setUid(u?.uid ?? null));
        return () => unsub();
    }, []);

    const cargarCount = async () => {
        if (!uid) { setCount(0); return; }
        try { setCount(await contarAlertasNoLeidas()); } catch (e) { console.error("AlertBell count error:", e); }
    };

    const cargarAlertas = async () => {
        if (!uid) { setAlertas([]); return; }
        setLoading(true); setErr("");
        try {
            setAlertas(await listarMisAlertas({ limit: 30 }));
        } catch (e) {
            console.error("AlertBell error:", e);
            setErr("No pude cargar tus alertas.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { cargarCount(); }, [uid]);
    useEffect(() => { if (open) cargarAlertas(); }, [open]);

    const marcarLeida = async (id) => {
        setAlertas((prev) => prev.map((a) => (a.id === id ? { ...a, leido: true } : a)));
        setCount((c) => Math.max(0, c - 1));
        try { await marcarAlertaLeida(id); } catch (e) { console.error(e); }
    };

    const marcarTodo = async () => {
        setAlertas((prev) => prev.map((a) => ({ ...a, leido: true })));
        setCount(0);
        try { await marcarTodasLeidas(); } catch (e) { console.error(e); }
    };

    if (!uid) return null;

    return (
        <>
            <div className="alertbell-wrap">
                <button
                    type="button"
                    className={`alertbell-btn${count > 0 ? " alertbell-btn--activo" : ""}`}
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Ver alertas de competencia"
                    title="Ver alertas de competencia"
                >
                    <span className="alertbell-icon" aria-hidden="true">🔔</span>
                    {count > 0 && <span className="alertbell-badge">{count > 99 ? "99+" : count}</span>}
                </button>
            </div>

            {open && (
                <div className="alertbell-panel" role="dialog" aria-label="Alertas de competencia">
                    <div className="alertbell-panel-head">
                        <div className="alertbell-panel-title">Alertas de competencia</div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            {count > 0 && (
                                <button type="button" className="alertbell-marcar-todo" onClick={marcarTodo}>
                                    Marcar todo leído
                                </button>
                            )}
                            <button type="button" className="alertbell-close" onClick={() => setOpen(false)} aria-label="Cerrar">✕</button>
                        </div>
                    </div>

                    {err ? (
                        <div className="alertbell-error">{err}</div>
                    ) : loading ? (
                        <div className="alertbell-hint">Cargando…</div>
                    ) : alertas.length === 0 ? (
                        <div className="alertbell-hint">Sin alertas todavía — agrega competidores en tus proyectos para empezar a recibirlas.</div>
                    ) : (
                        <div className="alertbell-list">
                            {alertas.map((a) => (
                                <Link
                                    key={a.id}
                                    to={`/mis-listas/${a.favoriteListId}`}
                                    className={`alertbell-item${a.leido ? "" : " alertbell-item--noleida"}`}
                                    onClick={() => { if (!a.leido) marcarLeida(a.id); setOpen(false); }}
                                >
                                    <span className="alertbell-item-icon">{ICONOS_EVENTO[a.tipoEvento] || "🔔"}</span>
                                    <div className="alertbell-item-body">
                                        <div className="alertbell-item-titulo">
                                            {nombreVehiculo(a.competidor || a.vehiculo)}
                                            {a.severidad === "alta" && <span className="alertbell-item-severidad">alta</span>}
                                        </div>
                                        <div className="alertbell-item-detalle">
                                            {a.valorAnterior && a.valorNuevo
                                                ? `${a.valorAnterior} → ${a.valorNuevo}`
                                                : a.valorNuevo || a.valorAnterior || ""}
                                            {a.deltaPorcentual != null && (
                                                <span> ({a.deltaPorcentual > 0 ? "+" : ""}{a.deltaPorcentual}%)</span>
                                            )}
                                        </div>
                                        <div className="alertbell-item-meta">
                                            En proyecto: {nombreVehiculo(a.vehiculo)} · {fmtFecha(a.createdAt)}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
